import * as THREE from 'three'
import { System } from './System'
import { Layers } from '../extras/Layers'

export class Boundary extends System {
  constructor(world) {
    super(world)

    this.world = world
    this.scene = world.stage.scene

    this.box = new THREE.Box3(new THREE.Vector3(-600, 0, -600), new THREE.Vector3(600, 5, 600))
    this.color = 0xff0000

    this.walls = []
    this.plotWalls = null // InstancedMesh built from setPlotGrid
    this.createWalls()
  }

  setPlotGrid(grid) {
    // remove any previous plot walls (geom + material are shared, dispose them via _plotResources)
    this._disposePlotWalls()
    if (!grid) {
      console.warn('[boundary] setPlotGrid called without grid')
      return
    }
    // hide the outer world walls so they don't obscure the plot grid
    this.setVisible(false)
    // expand the clamping box to the full plot grid so the player isn't locked in the default 1200-unit region
    const extent = grid.halfGrid + grid.size
    this.box.min.set(-extent, -100, -extent)
    this.box.max.set(extent, 5000, extent)
    const { size, perSide, halfGrid } = grid
    console.log(`[boundary] building plot grid: ${perSide} x ${perSide}, size=${size}, halfGrid=${halfGrid}`)
    const thickness = 0.5
    const height = 200
    const midY = height / 2
    const lineLen = size * perSide
    const linesPerSide = perSide + 1

    const wallMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.45,
      side: THREE.DoubleSide,
      depthWrite: false,
    })

    const vGeom = new THREE.BoxGeometry(thickness, height, lineLen)
    const hGeom = new THREE.BoxGeometry(lineLen, height, thickness)
    const out = []

    // vertical fence lines at x = -halfGrid, -halfGrid+size, ..., +halfGrid (covers all 4 sides)
    for (let i = 0; i < linesPerSide; i++) {
      const x = -halfGrid + i * size
      const m = new THREE.Mesh(vGeom, wallMat)
      m.position.set(x, midY, 0)
      m.frustumCulled = false
      m.name = `plot-wall-v-${i}`
      this.scene.add(m)
      out.push(m)
    }
    // horizontal fence lines at z = -halfGrid, ..., +halfGrid
    for (let i = 0; i < linesPerSide; i++) {
      const z = -halfGrid + i * size
      const m = new THREE.Mesh(hGeom, wallMat)
      m.position.set(0, midY, z)
      m.frustumCulled = false
      m.name = `plot-wall-h-${i}`
      this.scene.add(m)
      out.push(m)
    }

    this.plotWalls = out
    this._plotResources = { geoms: [vGeom, hGeom], mats: [wallMat] }
    console.log(`[boundary] plot grid ready: ${out.length} wall lines covering ${lineLen} x ${lineLen}`)

    // ensure there is a giant static physics floor under the entire grid so the avatar always has
    // something to land on (the scene model may not have a collider at every tile)
    this._ensureGroundPlane(lineLen)
  }

  _ensureGroundPlane(span) {
    if (this.groundActor) return
    if (typeof PHYSX === 'undefined' || !this.world.physics?.physics) return
    try {
      const halfX = span / 2
      const halfZ = span / 2
      const halfY = 0.5
      const transform = new PHYSX.PxTransform(PHYSX.PxIDENTITYEnum.PxIdentity)
      transform.p.x = 0
      transform.p.y = -halfY // top face sits at y=0
      transform.p.z = 0
      const actor = this.world.physics.physics.createRigidStatic(transform)
      const geometry = new PHYSX.PxBoxGeometry(halfX, halfY, halfZ)
      const material = this.world.physics.defaultMaterial
      const flags = new PHYSX.PxShapeFlags()
      flags.raise(PHYSX.PxShapeFlagEnum.eSCENE_QUERY_SHAPE | PHYSX.PxShapeFlagEnum.eSIMULATION_SHAPE)
      const layer = Layers.environment
      const pairFlags = PHYSX.PxPairFlagEnum.eNOTIFY_TOUCH_FOUND | PHYSX.PxPairFlagEnum.eNOTIFY_TOUCH_LOST | PHYSX.PxPairFlagEnum.eNOTIFY_CONTACT_POINTS
      const filterData = new PHYSX.PxFilterData(layer.group, layer.mask, pairFlags, 0)
      const shape = this.world.physics.physics.createShape(geometry, material, true, flags)
      shape.setQueryFilterData(filterData)
      shape.setSimulationFilterData(filterData)
      actor.attachShape(shape)
      this.groundHandle = this.world.physics.addActor(actor, { node: null, tag: 'plot-ground', playerId: null })
      this.groundActor = actor
      this.groundShape = shape
      PHYSX.destroy(geometry)
      console.log(`[boundary] static ground plane added (${span} x ${span})`)
    } catch (err) {
      console.error('[boundary] failed to add ground plane', err)
    }
  }

  _disposePlotWalls() {
    if (this.plotWalls) {
      for (const m of this.plotWalls) this.scene.remove(m)
      this.plotWalls = null
    }
    if (this._plotResources) {
      for (const g of this._plotResources.geoms) g.dispose()
      for (const m of this._plotResources.mats) m.dispose()
      this._plotResources = null
    }
    if (this.groundActor) {
      try {
        this.groundHandle?.destroy?.()
        this.groundShape?.release?.()
        this.groundActor.release?.()
      } catch (err) {
        console.error('[boundary] failed to release ground plane', err)
      }
      this.groundActor = null
      this.groundShape = null
      this.groundHandle = null
    }
  }

  isInsideXY(position) {
    console.log('isInsideXY called with position:', position)

    const x = position.x !== undefined ? position.x : position._x
    const y = position.y !== undefined ? position.y : position._y
    const z = position.z !== undefined ? position.z : position._z

    if (x === undefined || y === undefined || z === undefined) {
      console.log('Invalid position format')
      return false
    }

    const inside =
      x >= this.box.min.x &&
      x <= this.box.max.x &&
      y >= this.box.min.y &&
      y <= this.box.max.y &&
      z >= this.box.min.z &&
      z <= this.box.max.z

    console.log('Position is inside boundary:', inside)
    return inside
  }

  extend(factor = 15) {
    const center = this.getCenter()

    // Calculate current size
    const size = new THREE.Vector3()
    this.box.getSize(size)

    // Multiply size by factor (but clone before scaling)
    const newSize = size.clone().multiplyScalar(factor)

    // Calculate half of the new size
    const halfSize = newSize.clone().multiplyScalar(0.5)

    // Set new min and max from the center point
    const newMin = new THREE.Vector3().subVectors(center, halfSize)
    const newMax = new THREE.Vector3().addVectors(center, halfSize)

    this.set(newMin, newMax)

    console.log(`Boundary extended by factor ${factor}`)
}

  createWalls() {
    const min = this.box.min
    const max = this.box.max
    
  console.log('Creating walls with min/max:', min, max);

    const wallThickness = 0.3
    const wallHeight = 500
    const midY = min.y + wallHeight / 2

    const wallMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.2 })

    // Left wall
    const left = new THREE.Mesh(new THREE.BoxGeometry(wallThickness, wallHeight, max.z - min.z), wallMaterial)
    left.position.set(min.x + wallThickness / 2, midY, (min.z + max.z) / 2)

    this.scene.add(left)
    this.walls.push(left)

    // Right wall
    const right = new THREE.Mesh(new THREE.BoxGeometry(wallThickness, wallHeight, max.z - min.z), wallMaterial)
    right.position.set(max.x - wallThickness / 2, midY, (min.z + max.z) / 2)

    this.scene.add(right)
    this.walls.push(right)

    // Back wall
    const back = new THREE.Mesh(new THREE.BoxGeometry(max.x - min.x, wallHeight, wallThickness), wallMaterial)
    back.position.set((min.x + max.x) / 2, midY, min.z + wallThickness / 2)

    this.scene.add(back)
    this.walls.push(back)

    // Front wall
    const front = new THREE.Mesh(new THREE.BoxGeometry(max.x - min.x, wallHeight, wallThickness), wallMaterial)
    front.position.set((min.x + max.x) / 2, midY, max.z - wallThickness / 2)

    this.scene.add(front)
    this.walls.push(front)
  }

  set(min, max) {
  console.log('Boundary set called with:', min, max);
    this.box.min.copy(min)
    this.box.max.copy(max)

    // Remove old walls
    this.walls.forEach(wall => {
      this.scene.remove(wall)
      wall.geometry.dispose()
      wall.material.dispose()
    })
    this.walls = []

    this.createWalls()
  }

  setColor(color) {
    this.color = color
    this.walls.forEach(wall => {
      wall.material.color.set(color)
    })
  }

  getCenter() {
    const min = this.box.min;
    const max = this.box.max;

    const center = new THREE.Vector3(
        (min.x + max.x) / 2,
        (min.y + max.y) / 2,
        (min.z + max.z) / 2
    );

    return center;
}

  setVisible(visible) {
    this.walls.forEach(wall => {
      wall.visible = visible
    })
  }

  fixedUpdate(delta) {
    this.world.entities.items.forEach(entity => {
      if (!entity.position) return
      // skip players — their physics drives movement and gravity, clobbering velocity here freezes them
      if (entity.isPlayer) return

      let pos = entity.position
      if (pos.value && pos.value instanceof THREE.Vector3) pos = pos.value

      // Clamp position within horizontal boundary
      pos.x = Math.max(this.box.min.x, Math.min(this.box.max.x, pos.x))
      pos.z = Math.max(this.box.min.z, Math.min(this.box.max.z, pos.z))
      // Optionally clamp vertical position if needed
      pos.y = Math.max(this.box.min.y, Math.min(this.box.max.y, pos.y))

      // Stop movement
      if (entity.velocity?.value) {
        entity.velocity.value.set(0, 0, 0)
      }

      if (entity.position.set) {
        entity.position.set(pos.x, pos.y, pos.z)
      } else if (entity.position.copy) {
        entity.position.copy(pos)
      }
    })
  }

  destroy() {
    this.walls.forEach(wall => {
      this.scene.remove(wall)
      wall.geometry.dispose()
      wall.material.dispose()
    })
    this.walls = []
    this._disposePlotWalls()
  }
}
